import { Box, Grid, ListItemButton } from '@mui/material';
import { GridColDef, GridRowId, GridSortModel } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { MdContactMail } from 'react-icons/md';
import { bulkTagContacts, ColumnSettings, CrmJobDTO } from 'wisestep-api';
import {
  FieldOption,
  PopoverOnClick,
  WsDataGrid,
  WsDrawer,
  WsIconMoreVer,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
  getDateUsingFormat,
  WsButton,
  WsColors,
} from 'wisestep-base';

import { ClintPageBreadCrumb } from '../../../features/clientDetails/helper/clientDetailsHelper';
import {
  defaultColumnSettings,
  defaultCrmJobsListColumnSettings,
  saveColumnSettingsToDB,
} from '../../../features/clientListingPage/helper/ClientListingHelper';
import WsLink from '../../../features/teams/components/WsLink';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { DrawerType, HalfPageBottomBlueBar } from '../helper/crmJobHelper';

import BulkTagModal from './BulkTagModal';
import ConvertToLeadDrawer from './ConvertToLeadDrawer';
import CrmContactsDrawer from './CrmContactsDrawer';
import CrmJobDialog from './CrmJobDialog';

interface ICrmJobsProps {
  crmJobsData: CrmJobDTO[];
  totalRecord: number;
  sortModel: GridSortModel;
  setSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  fetchCrmJobs: () => void;
}

const CrmJobData = ({
  crmJobsData,
  totalRecord,
  sortModel,
  setSortModel,
  fetchCrmJobs,
}: ICrmJobsProps) => {
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [settings, setSettings] = useState<FieldOption[]>(defaultCrmJobsListColumnSettings);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [crmJobId, setCrmJobId] = useState<string>();
  const [convertToLeadId, setConvertToLeadId] = useState<string>();
  const [bulkTagLoading, setBulkTagLoading] = useState<boolean>(false);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [activeDrawer, setActiveDrawer] = useState<DrawerType>(null);

  const onColumnSettingsReset = () => {
    saveColumnSettingsToDB(
      defaultColumnSettings(),
      ColumnSettings.CRM_JOB_LISTING,
      setNotification
    );
    setSettings(defaultCrmJobsListColumnSettings);
  };

  useEffect(() => {
    setRowSelectionModel(selectedJobIds);
  }, [selectedJobIds]);

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setPageNo(0);
    setSortModel(sortModel);
  };

  const handleAssignUser = (params) => {
    setCrmJobId(params.id as string);
    setActiveDrawer('eligibleContacts');
  };

  const handleTaggedContactsClick = (params) => {
    setCrmJobId(params.id as string);
    setActiveDrawer('taggedContacts');
  };

  const handleConvertToLead = (crmJobId: string) => {
    setConvertToLeadId(crmJobId);
    setActiveDrawer('convertToLead');
  };

  const handleSelectionChange = (newSelectionModel: GridRowId[]) => {
    setRowSelectionModel(newSelectionModel);
    setSelectedJobIds(newSelectionModel as string[]);
  };

  const handleBulkTag = async () => {
    setBulkTagLoading(true);
    try {
      await bulkTagContacts({ jobIds: selectedJobIds });
      setActiveDrawer(null);
      setSelectedJobIds([]);
      fetchCrmJobs();
      setNotification({ message: 'Bulk tagging successful', type: 'success' });
    } catch (error) {
      setNotification({ message: 'Bulk tagging failed', type: 'error' });
    } finally {
      setBulkTagLoading(false);
    }
  };

  const renderLinkTypography = (href: string, text: string) => (
    <WsLink to={href}>
      <WsTypography
        variant={WsTypoVariants.SUB_TITLE}
        bold={false}
        hoverText={text}
        color={WsTypoColors.PRIMARY}
        cursor="pointer"
      >
        {text}
      </WsTypography>
    </WsLink>
  );

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Job Title',
      flex: 1.5,
      renderCell: (params) => {
        const crmJobInfo = crmJobsData?.[params.row.rowIndex];
        return (
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Box overflow="hidden">
              {renderLinkTypography(`/leads/details?leadId=${params.row.id}`, params.row['title'])}
            </Box>
            <CrmJobDialog jobInfo={crmJobInfo} />
          </Box>
        );
      },
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'jobStatus',
      headerName: 'Job Status',
      flex: 1,
    },
    {
      field: 'clientName',
      headerName: 'Client Name',
      flex: 1.5,
      renderCell: (params) => {
        const client = crmJobsData?.[params.row.rowIndex]?.client;
        const isChild = client?.parentId == null;
        return renderLinkTypography(
          `/client-details?clientId=${client?.id}&isChild=${isChild}&tabIndex=0`,
          params.row['clientName']
        );
      },
    },
    {
      field: 'contacts',
      headerName: 'Contacts',
      width: 90,
      renderCell: (params) => {
        const client = crmJobsData?.[params.row.rowIndex]?.client;
        const isChild = client?.parentId == null;
        return (
          <WsLink
            to={`/client-details?tabIndex=${
              isChild ? 5 : 4
            }&isProfile=true&isChild=${isChild}&name=${client?.name}&clientId=${
              client?.id
            }&breadCrumbList=${getEncodedQueryParams(ClintPageBreadCrumb['CLIENTS'])}`}
          >
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              cursor="pointer"
              color={WsTypoColors.PRIMARY}
            >
              {params.row['contacts']}
            </WsTypography>
          </WsLink>
        );
      },
    },
    {
      field: 'eligibleContacts',
      headerName: 'Eligible Contacts',
      flex: 1,
      renderCell: (params) => (
        <WsTypography
          onClick={() => handleAssignUser(params)}
          color={WsTypoColors.PRIMARY}
          variant={WsTypoVariants.SUB_TITLE}
          cursor="pointer"
        >
          {params.row['eligibleContacts']}
        </WsTypography>
      ),
    },
    {
      field: 'taggedContacts',
      headerName: 'Mailing Contacts',
      flex: 1,
      renderCell: (params) => (
        <WsTypography
          onClick={() => handleTaggedContactsClick(params)}
          color={WsTypoColors.PRIMARY}
          variant={WsTypoVariants.SUB_TITLE}
          cursor="pointer"
        >
          {params.row['taggedContacts']}
        </WsTypography>
      ),
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 1,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
    },
    {
      field: 'action',
      headerName: '',
      width: 40,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <PopoverOnClick element={<WsIconMoreVer />}>
          <ListItemButton onClick={() => handleConvertToLead(params.row.id)}>
            Convert to Lead
          </ListItemButton>
        </PopoverOnClick>
      ),
    },
  ];

  const tableData = crmJobsData?.map((job, index) => ({
    id: job.id,
    title: job.title,
    client: job.client,
    clientName: job.client?.name,
    contactName: job.clientContact
      ? `${job.clientContact?.firstName} ${job.clientContact?.lastName ?? ''}`
      : '',
    createdOn:
      job?.createdOn != undefined ? getDateUsingFormat(job.createdOn?.toString(), 'DD-MMM-YY') : '',
    createdByName: job.createdByName,
    createdBy: job.createdBy,
    rowIndex: index,
    location: !isEmpty(job.location)
      ? `${job.location?.city ?? ''} ${job.location?.state ?? ''} ${job.location?.country ?? ''}`
      : '',
    contacts: job.clientContactCount,
    eligibleContacts: job.eligibleContactCount,
    taggedContacts: job.taggedContactCount,
    tags: job.tags,
  }));

  return (
    <>
      <WsDataGrid
        checkboxSelection
        autoHeight
        rows={tableData ?? []}
        columns={columns}
        paginationMode="server"
        page={pageNo}
        onPageChange={setPageNo}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        selectionModel={rowSelectionModel}
        onSelectionModelChange={handleSelectionChange}
        totalRows={totalRecord}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        columnVisibility
        columnSettings={settings}
        onColumnSettingsChange={(data) => {
          saveColumnSettingsToDB(data, ColumnSettings.CRM_JOB_LISTING, setNotification);
          setSettings(data);
        }}
        onColumnSettingsReset={onColumnSettingsReset}
      />
      {selectedJobIds.length > 0 && (
        <HalfPageBottomBlueBar>
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Grid item>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.WHITE}>
                {selectedJobIds.length} Job(s) Selected
              </WsTypography>
            </Grid>
            <Grid item>
              <WsButton onClick={() => setActiveDrawer('bulkTag')}>Bulk Transfer</WsButton>
            </Grid>
          </Grid>
        </HalfPageBottomBlueBar>
      )}
      {activeDrawer === 'bulkTag' && (
        <BulkTagModal
          open={activeDrawer === 'bulkTag'}
          onClose={() => setActiveDrawer(null)}
          onConfirm={handleBulkTag}
          loading={bulkTagLoading}
          title={`Bulk Transfer - ${selectedJobIds.length} Job${
            selectedJobIds.length !== 1 ? 's' : ''
          }`}
          description="All eligible contacts for selected jobs will be moved to mailing contacts."
          confirmText={bulkTagLoading ? 'Moving...' : 'Bulk Transfer'}
          dismissText="Cancel"
          icon={<MdContactMail size={30} color={WsColors.GenericRichBlack} />}
        />
      )}
      {activeDrawer === 'eligibleContacts' && crmJobId && (
        <WsDrawer
          open={true}
          onClose={() => {
            setActiveDrawer(null);
            fetchCrmJobs();
          }}
          title="Eligible Contacts Access"
          headerSize="medium"
          size="medium"
        >
          <CrmContactsDrawer jobId={crmJobId} />
        </WsDrawer>
      )}
      {activeDrawer === 'taggedContacts' && crmJobId && (
        <WsDrawer
          open={true}
          onClose={() => {
            setActiveDrawer(null);
            fetchCrmJobs();
          }}
          title="Mailing Contacts"
          headerSize="medium"
          size="medium"
        >
          <CrmContactsDrawer jobId={crmJobId} defaultTab={1} />
        </WsDrawer>
      )}
      {activeDrawer === 'convertToLead' && convertToLeadId && (
        <ConvertToLeadDrawer
          open={true}
          onClose={() => setActiveDrawer(null)}
          crmJobId={convertToLeadId}
        />
      )}
    </>
  );
};

export default CrmJobData;
